import { MerchantBusinessType } from "@/constants/enum";

// LaunchDarkly feature flag names
export const FEATURE_FLAG_PAZE_ON_COLO2 = "PAZE_ON_COLO2";
export const FEATURE_FLAG_HORIZONTAL_SCROLL_ON_COLO2 =
  "HORIZONTAL_SCROLL_ON_COLO2";
export const FEATURE_FLAG_QUANTUM_METRIC = "2025-quantum-metric";
export const FEATURE_FLAG_LOCALIZATION_ON_COLO2 =
  "SERVICES_FRENCH_LOCALIZATION_ENABLED";
export const DEPOSIT_TAKING_FOR_SERVICES_ENABLED =
  "DEPOSIT_TAKING_FOR_SERVICES_ENABLED";
export const OLO_DISPLAY_SPECIAL_INSTRUCTIONS_FIELD_SETTINGS =
  "OLO_DISPLAY_SPECIAL_INSTRUCTIONS_FIELD_SETTINGS";
// Gates the bot-neutralization redirect/read path (early error prevention).
// Read from the resolved merchant's launchDarklyFeatures; see
// isEarlyErrorPreventionEnabled in botNeutralization.ts. Doubles as the name of a
// server-level env mirror: at the proxy/middleware layer no merchant is resolved,
// so setting EARLY_ERROR_PREVENTION_ENABLED=true lets those layers honor a
// fleet-wide rollout of the flag. Unset, the merchant-less layers read as OFF.
export const FEATURE_FLAG_EARLY_ERROR_PREVENTION =
  "EARLY_ERROR_PREVENTION_ENABLED";
export const FEATURE_FLAG_AU_SURCHARGE_WARNING = "HIDE_AU_SURCHARGE_WARNING";
export const FEATURE_FLAG_2026_COLO_SAVED_CARD_ON_FILE_ENABLED =
  "2026_COLO_SAVED_CARD_ON_FILE_ENABLED";
export const FEATURE_FLAG_ADVANCED_ORDERING = "INCREASED_ADVANCED_ORDERS";

export const FEATURE_FLAG_QUICK_REORDER = "2026_QUICK_REORDER_ENABLED";
export const REORDER_ITEMS_DISPLAY_COUNT = 4;
export const ORDER_AGAIN_CATEGORY_ID = "ORDERAGAIN";

// COLOv3 migration flags complement the local CSV lists. The global flag is
// the LaunchDarkly prerequisite for each zone-specific migration flag.
export const FEATURE_FLAG_COLOV3_MIGRATED = "COLOV3_MIGRATED";
export const FEATURE_FLAG_COLOV3_RESTAURANT_MIGRATED =
  "COLOV3_RESTAURANT_MIGRATED";
export const FEATURE_FLAG_COLOV3_RETAIL_MIGRATED = "COLOV3_RETAIL_MIGRATED";
export const FEATURE_FLAG_COLOV3_SERVICES_MIGRATED = "COLOV3_SERVICES_MIGRATED";
export const FEATURE_FLAG_COLOV3_CARTAPI_MIGRATED = "COLOV3_CARTAPI_MIGRATED";

export const SHIPPING_LINE_ITEM_TYPE = "SHIPPING";
export const FEATURE_FLAG_SHIPPING = "SHIPPING_INTEGRATION_ENABLED";

export const FEATURE_FLAG_ITEM_MARKER = "ITEM_MARKER_ENABLED";

export const DEFAULT_ID = "default";

export const NAV_ACTION = {
  SEARCH: "search",
  CART: "cart",
  ACCOUNT: "account",
  MOBILE_MENU: "mobile-menu",
  MOBILE_CLOSE: "mobile-close",
  DAYPART: "daypart",
  CATEGORY_SEARCH: "category-search",
} as const;

export const NAV_SLOT = {
  PERKS: "perks",
  LANGUAGE: "language",
  ACCOUNT: "account",
} as const;

// Routes
export const ROUTE_HOME = "/";
export const ROUTE_RETAIL =
  process.env.NEXT_PUBLIC_ALLOW_SHOP_AS_DEFAULT_RETAIL_ROUTE === "true"
    ? "/shop"
    : "";
export const ROUTE_SERVICES = "/services";
export const ROUTE_ALL = "/all";
export const ROUTE_SERVICES_ALL = `${ROUTE_SERVICES}/all`;
export const ROUTE_MENU = (restaurantMfeEnabled: boolean) =>
  restaurantMfeEnabled ? "/order" : "/menu";
export const ROUTE_MENU_ALL = (restaurantMfeEnabled: boolean) =>
  `${ROUTE_MENU(restaurantMfeEnabled)}/all`;
export const RESTAURANT_MENU_COOKIE_PATHS = [
  ROUTE_MENU(true),
  ROUTE_MENU_ALL(true),
  ROUTE_MENU(false),
  ROUTE_MENU_ALL(false),
] as const;
export const ROUTE_SHOP = "/shop";
export const ROUTE_CATEGORIES = "/categories";
export const ROUTE_CATEGORIES_ALL = `${ROUTE_CATEGORIES}/all`;
export const ROUTE_CONFIRMATION = "/confirmation";
export const ROUTE_NOT_FOUND = "/not-found";
export const ROUTE_PLEASE_WAIT = "/please-wait";
export const ROUTE_GUEST_CHECKOUT = "/guest-checkout";
export const ROUTE_CHECKOUT = "/checkout";
export const ROUTE_CUSTOMER_PROFILE = "/customer-profile";
export const ROUTE_MY_ORDERS = "/my-orders";
export const ROUTE_MY_APPOINTMENTS = "/my-appointments";
export const ROUTE_VERIFY_EMAIL = "/verify-email";
export const ROUTE_UNCATEGORISED = "Uncategorized";
export const ROUTE_GIFTCARD = "/giftcard";
export const ROUTE_ABOUT = "/about";
export const ROUTE_POLICY_RETURNS = "/policy/returns";
export const ROUTE_POLICY_TERMS = "/policy/terms";
export const ROUTE_EXPIRED = "/expired";
export const ROUTE_NOT_PUBLISHED = "/not-published";

// Services zone phased migration (CSV/LaunchDarkly-driven, per-merchant).
// SERVICES_MIGRATION_DIR: workspace-relative folder holding phase CSV files; each
//   file contributes merchant IDs to the in-memory migration set.
// SERVICES_ZONE_ORIGIN: the NAME of the env var that points at the services MFE
//   origin (read at runtime, e.g. process.env[SERVICES_ZONE_ORIGIN]).
// *_REWRITE_QUERY_*: the `mfe_services` query/cookie marker used to hand off a
//   migrated merchant to the services zone and to roll the handoff back.
export const SERVICES_MIGRATION_DIR = "src/migration/services";
export const SERVICES_ZONE_ORIGIN = "SERVICES_ZONE_ORIGIN";
export const SERVICES_MIGRATION_REWRITE_QUERY_KEY = "mfe_services";
export const SERVICES_MIGRATION_REWRITE_QUERY_VALUE = "1";
export const SERVICES_MIGRATION_REWRITE_QUERY_DISABLE_VALUE = "0";
export const SERVICES_MIGRATION_COOKIE_KEY = "mfe_services";
export const SERVICES_MIGRATION_COOKIE_VALUE = "1";

// Restaurant zone phased migration (CSV/LaunchDarkly-driven, per-merchant).
// RESTAURANT_MIGRATION_DIR: workspace-relative folder holding phase CSV files; each
//   file contributes merchant IDs to the in-memory migration set.
// RESTAURANT_ZONE_ORIGIN: the NAME of the env var that points at the restaurant MFE
//   origin (read at runtime, e.g. process.env[RESTAURANT_ZONE_ORIGIN]).
// *_REWRITE_QUERY_*: the `mfe_restaurant` query/cookie marker used to hand off a
//   migrated merchant to the restaurant zone and to roll the handoff back.
export const RESTAURANT_MIGRATION_DIR = "src/migration/restaurant";
export const RESTAURANT_ZONE_ORIGIN = "RESTAURANT_ZONE_ORIGIN";
export const RESTAURANT_MIGRATION_REWRITE_QUERY_KEY = "mfe_restaurant";
export const RESTAURANT_MIGRATION_REWRITE_QUERY_VALUE = "1";
export const RESTAURANT_MIGRATION_REWRITE_QUERY_DISABLE_VALUE = "0";
export const RESTAURANT_MIGRATION_COOKIE_KEY = "mfe_restaurant";
export const RESTAURANT_MIGRATION_COOKIE_VALUE = "1";

// Retail zone phased migration (CSV/LaunchDarkly-driven, per-merchant).
// RETAIL_MIGRATION_DIR: workspace-relative folder holding phase CSV files; each
//   file contributes merchant IDs to the in-memory migration set.
// RETAIL_ZONE_ORIGIN: the NAME of the env var that points at the retail MFE
//   origin (read at runtime, e.g. process.env[RETAIL_ZONE_ORIGIN]).
// *_REWRITE_QUERY_*: the `mfe_retail` query/cookie marker used to hand off a
//   migrated merchant to the retail zone and to roll the handoff back.
// Unlike services/restaurant, retail routes are unnamespaced (no basePath), so
// the handoff preserves the incoming pathname and only appends the marker.
export const RETAIL_MIGRATION_DIR = "src/migration/retail";
export const RETAIL_ZONE_ORIGIN = "RETAIL_ZONE_ORIGIN";
export const RETAIL_MIGRATION_REWRITE_QUERY_KEY = "mfe_retail";
export const RETAIL_MIGRATION_REWRITE_QUERY_VALUE = "1";
export const RETAIL_MIGRATION_REWRITE_QUERY_DISABLE_VALUE = "0";
export const RETAIL_MIGRATION_COOKIE_KEY = "mfe_retail";
export const RETAIL_MIGRATION_COOKIE_VALUE = "1";

// Duda redirection for retail merchants with Duda storefronts
export const DUDA_REDIRECT_COOKIE_KEY = "dudasubdomain";

// Top-level path segments the shell owns and must NEVER hand off to the retail
// zone. Retail's dynamic `/[slug]` route matches almost anything, so the layout
// handoff guard and the next.config `/:slug` rewrite regex both exclude these
// reserved segments. This list is the single source of truth for both — keep it
// in sync to avoid over-capturing shell/other-zone routes.
export const NON_RETAIL_ROUTES = [
  "api",
  "_next",
  "retail-static",
  "checkout",
  "confirmation",
  "services",
  "menu",
  "order",
  "giftcard",
  "guest-checkout",
  "verify-email",
  "about",
  "policy",
  "my-orders",
  "my-appointments",
  "customer-profile",
  "chat",
  "chatbot",
  "merchantfinder",
  "scheduler",
  "invoice-payment",
  "not-found",
  "please-wait",
  "expired",
  "not-published",
  "favicon.ico",
  "sitemap.xml",
  "sitemap.xml.gz",
  "robots.txt",
  "demo",
  "pip-board",
] as const;

export const UNCATEGORISED_CATEGORY_ID = "uncategorised";
export const UNCATEGORISED_CACHE_KEY = "uncategorized";

export const RETAIL_ITEMS_FETCH_LIMIT = "999";

export const GOOGLEMAP_URL = "https://maps.googleapis.com/maps/api/js";
export const PRIVACY_POLICY_URL = "https://www.clover.com/privacy-policy";

// Time constants for calculations
export const TIME_CONSTANTS = {
  MINUTES_PER_HOUR: 60,
  MINUTES_PER_DAY: 1440,
  HOURS_PER_DAY: 24,
  SECONDS_PER_MINUTE: 60,
  MILLISECONDS_PER_SECOND: 1000,
  MILLISECONDS_PER_MINUTE: 60 * 1000, // 60000
} as const;

export const DATE_FORMAT_ISO = 'yyyy-MM-dd';

export const DELIVERY_TIP_SUGGESTIONS = [
  {
    id: "FB00MRRQF32PG",
    name: null,
    percentage: 15,
  },
  {
    id: "08F1QBKDN84ZJ",
    name: null,
    percentage: 18,
  },
  {
    id: "M0GQ9889NJ5Q4",
    name: null,
    percentage: 20,
  },
  {
    id: "TK05FMS88WAX0",
    name: null,
    percentage: 25,
  },
];

// API routes
export const API_ROUTE_APPOINTMENTS = "/api/appointments";

export const EMAIL_TYPO_MAP: Record<string, string> = {
  con: "com",
  vom: "com",
  clm: "com",
  comp: "com",
  col: "com",
  fom: "com",
  cim: "com",
  xom: "com",
  gom: "com",
  ckm: "com",
  come: "com",
  cok: "com",
  cpm: "com",
  comm: "com",
  conm: "com",
  vo: "com",
  ney: "net",
  ner: "net",
  nsu: "net",
  ed: "edu",
  eud: "edu",
  du: "edu",
  ogr: "org",
  or: "org",
  og: "org",
  urg: "org",
  rog: "org",
  couk: "co.uk",
  "co.um": "co.uk",
  "co.u": "co.uk",
  "cp.uk": "co.uk",
};

export const MERCHANT_TYPES = {
  HYBRID: "HYBRID",
  SERVICE: "SERVICE",
  RESTAURANT: "RESTAURANT",
  ESSENTIAL: "ESSENTIAL",
  SERVICE_RETAIL_ONLY: "SERVICE_RETAIL_ONLY",
  RETAIL: "RETAIL",
} as const;

// Product Types
export const PRODUCT_TYPE_SERVICE = "SERVICE";
export const PRODUCT_TYPE_REGULAR = "REGULAR";

// Path Segments
export const PATH_SERVICES = "services";
export const PATH_SHOP = "shop";
export const PATH_MENU = "menu";
export const PATH_CATEGORIES = "categories";

// Template Names
export const TEMPLATE_IMAGE_FOCUSED = "image focused";
export const TEMPLATE_DESCRIPTION_FOCUSED = "description focused";

// Merchant plan labels reported to QuantumMetric (plan event) and rendered on
// the /pip-board diagnostics page. Derived from Merchant.merchantBusinessType
// via MERCHANT_PLAN_BY_BUSINESS_TYPE. Changing a label changes the string that
// QuantumMetric records and the value shown on the board.
export const MERCHANT_PLAN_SERVICE = "Services Growth";
export const MERCHANT_PLAN_RESTAURANT = "Restaurant Growth";
export const MERCHANT_PLAN_RETAIL = "Retail Growth";
export const MERCHANT_PLAN_ESSENTIALS = "Essentials";
// Fallback label when merchantBusinessType is absent or unrecognised.
export const MERCHANT_PLAN_UNKNOWN = "unknown";

// Maps a merchant's business type to its plan label. A hybrid merchant reports
// business type SERVICE ("2") with an item bitmap of 6, so it maps to the
// Services Growth plan. Consumers should fall back to MERCHANT_PLAN_UNKNOWN when
// a type has no entry.
export const MERCHANT_PLAN_BY_BUSINESS_TYPE: Record<
  MerchantBusinessType,
  string
> = {
  [MerchantBusinessType.SERVICE]: MERCHANT_PLAN_SERVICE,
  [MerchantBusinessType.RESTAURANT]: MERCHANT_PLAN_RESTAURANT,
  [MerchantBusinessType.RETAIL]: MERCHANT_PLAN_RETAIL,
  [MerchantBusinessType.ESSENTIAL_MERCHANT]: MERCHANT_PLAN_ESSENTIALS,
};

export const DEFAULT_PAGE_BG_COLOR = "#FBFBFB";
export const DEFAULT_DUDA_PAGE_BG_COLOR = "#F5F5F5";

export const WEBSITE_UNPUBLISHED_ERROR_MESSAGE = "not yet published";

const PROTECTED_ROUTES = [
  ROUTE_CHECKOUT,
  ROUTE_CUSTOMER_PROFILE,
  ROUTE_MY_ORDERS,
  ROUTE_MY_APPOINTMENTS,
  ROUTE_VERIFY_EMAIL,
];

const INTERNAL_ROUTES = [
  ROUTE_MENU(false),
  ROUTE_MENU(true),
  ROUTE_MENU_ALL(false),
  ROUTE_MENU_ALL(true),
  ROUTE_MY_APPOINTMENTS,
  ROUTE_MY_ORDERS,
  ROUTE_SERVICES,
  ROUTE_RETAIL,
  ROUTE_CONFIRMATION,
  ROUTE_EXPIRED,
  ROUTE_GUEST_CHECKOUT,
  ROUTE_GIFTCARD,
  ROUTE_UNCATEGORISED,
  ROUTE_PLEASE_WAIT,
  ROUTE_CATEGORIES,
  ROUTE_CATEGORIES_ALL,
  ROUTE_ALL,
  ROUTE_NOT_PUBLISHED,
  ROUTE_PLEASE_WAIT
];

// Relative to getOloApiEndpoint(), which already includes the service prefix.
export const SHIPPING_METHODS_API_PATH = (merchantId: string) =>
  `/v1/merchants/${merchantId}/shipment-manager/shippingMethods`;

// Rates are needed before checkout can proceed; abort a stalled fetch after 10s.
// Adjust this budget here without changing the estimate/order request timeouts.
export const SHIPPING_METHODS_TIMEOUT_MS = 10_000;

export const IS_INTERNAL_OR_PROTECTED_ROUTE = (path: string): boolean => {
  if (!path) return true; // Treat empty path as internal/protected

  const allRoutes = [...INTERNAL_ROUTES, ...PROTECTED_ROUTES];
  const firstSegment = path.split("/")[1] ?? "";

  // Check if the first segment matches any route (case-insensitive)
  return allRoutes.some(route => {
    const routeSegment = route.split("/")[1] ?? route;
    return routeSegment.toLowerCase() === firstSegment.toLowerCase();
  });
};
